import React from 'react';
import { Spinner, Box } from '@amzn/awsui-components-react-v3';
import StuckWorkflowView from './StuckWorkflowView';
import Timestamp from '../common/Timestamp';
import { useStuckFilters } from './useStuckFilters';
import { useStuckData } from './useStuckData';
import { REALM_TO_REGION_CODE } from './constants';
import './StuckWorkflow.scss';

interface Props {
    stage: string;
    region: string; // realm from the app env selector; the backend is deployed per-region and returns that region's stuck data
}

// Scope: stuck refund workflows (PCES-Retry + PMRAS-ProcessPMR) for the deployed region.
const StuckWorkflow: React.FC<Props> = ({ stage, region }) => {
    const filterApi = useStuckFilters();
    const { rows, history, tickets, status, generatedAt, error, refresh } = useStuckData(stage, region);
    const regionCode = REALM_TO_REGION_CODE[region] || region;
    const contextLabel = `${regionCode} · ${stage}`;

    const banner = (status === 'loading' || status === 'refreshing')
        ? `Loading stuck workflow data — ${contextLabel}…`
        : status === 'live'
            ? <>Live data — {contextLabel}{generatedAt ? <> · generated <Timestamp date={generatedAt} /></> : null}.</>
            : `⚠ No live data — backend unreachable${error ? ` (${error})` : ''} for ${contextLabel}.`;
    const bannerClass = status === 'error' ? 'sw-banner sw-banner--warning' : status === 'live' ? 'sw-banner sw-banner--live' : 'sw-banner';

    return (
        <div className="stuck-workflow">
            <div className={bannerClass}>
                {banner} Env: {region}.
                {status === 'error' && (
                    <>
                        {' '}
                        <button type="button" className="sw-retry" onClick={refresh}>Retry</button>
                    </>
                )}
            </div>

            {(status === 'loading' || status === 'refreshing') ? (
                <Box textAlign="center" padding="xxl">
                    <Spinner size="large" /> Loading stuck workflow data…
                </Box>
            ) : status === 'error' ? (
                <Box textAlign="center" padding="xxl" color="text-status-inactive">
                    Could not load stuck workflow data. Use Retry above once the backend is reachable.
                </Box>
            ) : (
                <StuckWorkflowView
                    filters={filterApi.filters}
                    contextLabel={contextLabel}
                    allRows={rows}
                    history={history}
                    tickets={tickets}
                    setFacet={filterApi.setFacet}
                    clearFacets={filterApi.clearFacets}
                />
            )}
        </div>
    );
};

export default StuckWorkflow;
